import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container, Grid, Typography, Button, Divider, Hidden, InputBase } from '@material-ui/core';
import clsx from 'clsx';
import { ScriptOptions } from 'commons/constants/KeyFlowConstants';
import CodeVO from 'commons/vo/CodeVO';
import WkblScriptParamVO from 'commons/vo/param/WkblScriptParamVO';

import { useWkblScript } from 'hooks';
import { RootState } from 'redux/reducers/type';
import { CancelIcon, SearchIcon } from 'asset/images';

import { HtmlTooltip } from 'components/Tooltip';
import { useStyles } from 'components/SearchFilter';
import { RecordSelect, SeasonSelect } from 'components/Select';
import { FromToDate } from 'components/Date';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

function SearchFilterByRecord() {
  const classes = useStyles();
  const { wkblSeasonList: seasonList } = useSelector((state: RootState) => state.code);
  const {
    wkblScriptList: scriptList,
    pagination,
    searchCondition,
  } = useSelector((state: RootState) => state.wkblScript);

  const { onRequestWkblScriptList, onChangeWkblScriptPage } = useWkblScript();

  const [selectedAllRecord, setSelectedAllRecord] = useState<boolean>(true);
  const [records, setRecords] = useState<string[]>(() => {
    const initList: string[] = [];
    // [초기 기록을 선택된 상태로 바꿀 경우 주석 해제]
    // ScriptOptions.forEach(item => {
    //   initList.push(item.value);
    // });
    return initList;
  });
  const [season, setSeason] = useState<string>('all');
  const [keywordsByPlayer, setKeywordsByPlayer] = useState<string>('');
  const [gameDateFrom, setGameDateFrom] = useState<Date | null>(null);
  const [gameDateTo, setGameDateTo] = useState<Date | null>(null);

  // 가리기 - 보이기 토글버튼
  const [isShowRecord, setIsShowRecord] = useState<boolean>(false);
  const [isShowSeason, setIsShowSeason] = useState<boolean>(false);

  const formattedGameDateFrom = useMemo(() => {
    if (gameDateFrom) {
      const year = gameDateFrom.getFullYear();
      const month = gameDateFrom.getMonth() + 1;
      const day = gameDateFrom.getDate();
      const result = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
      return result;
    } else {
      return '';
    }
  }, [gameDateFrom]);

  const formattedGameDateTo = useMemo(() => {
    if (gameDateTo !== null) {
      const year = gameDateTo.getFullYear();
      const month = gameDateTo.getMonth() + 1;
      const day = gameDateTo.getDate();
      const result = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
      return result;
    } else {
      return '';
    }
  }, [gameDateTo]);

  const gameDateFromParam = useMemo(() => {
    const result = formattedGameDateFrom.replace(/-/g, '');
    if (result.length === 0) {
      return undefined;
    }
    return result;
  }, [formattedGameDateFrom]);

  const gameDateToParam = useMemo(() => {
    const result = formattedGameDateTo.replace(/-/g, '');
    if (result.length === 0) {
      return undefined;
    }
    return result;
  }, [formattedGameDateTo]);

  const search = (pageNumber: number) => {
    const searchParam = new WkblScriptParamVO({
      scripts: records,
      season,
      gameDateFrom: gameDateFromParam,
      gameDateTo: gameDateToParam,
      keywords: keywordsByPlayer ? [keywordsByPlayer] : [],
      page: pageNumber,
      retr: searchCondition.retr,
      sort: searchCondition.sort,
      asc: searchCondition.asc,
    });
    onRequestWkblScriptList(searchParam);
  };

  useEffect(() => {
    search(searchCondition.page);
  }, [searchCondition.page]);

  useEffect(() => {
    onClickSearch();
  }, [
    records,
    season,
    gameDateFrom,
    gameDateTo,
    // keywordsByPlayer,
    searchCondition.retr,
    searchCondition.sort,
    searchCondition.asc,
  ]);

  useEffect(() => {
    // 없는 페이지 번호로 호출시 1페이지로 재검색 (1페이지를 요청하지 않은 경우)
    if (searchCondition.page > 0 && searchCondition.page + 1 > pagination.pageCount) {
      onChangeWkblScriptPage(0);
    }
  }, [pagination]);

  useEffect(() => {
    if (records.length === ScriptOptions.length) {
      setSelectedAllRecord(true);
    } else {
      setSelectedAllRecord(false);
    }
  }, [records]);

  const onClickSearch = () => {
    if (searchCondition.page === 0) {
      search(0);
    } else {
      onChangeWkblScriptPage(0);
    }
  };

  const onSubmitSearch = useCallback(
    e => {
      e.preventDefault();

      onClickSearch();

      return false; // Block form forward
    },
    [keywordsByPlayer],
  );

  const onClickRecord = useCallback(
    event => {
      const { value } = event.currentTarget;
      if (value) {
        const param: string[] = [...records];

        const index = param.indexOf(value);
        if (index !== -1) {
          const first = param.slice(0, index);
          const second = param.slice(index + 1);
          setRecords([...first, ...second]);
        } else {
          param.push(value);
          setRecords(param);
        }
      } else {
        setRecords([]);
      }
    },
    [records],
  );

  const onSelectRecord = useCallback(
    (value: string) => {
      // const { value } = event.currentTarget;
      if (value) {
        const param: string[] = [...records];

        const index = param.indexOf(value);
        if (index !== -1) {
          const first = param.slice(0, index);
          const second = param.slice(index + 1);
          setRecords([...first, ...second]);
        } else {
          param.push(value);
          setRecords(param);
        }
      } else {
        setRecords([]);
      }
    },
    [records],
  );

  const onClickSeason = useCallback(event => {
    const { value } = event.currentTarget;
    setSeason(value);
  }, []);

  const onChangeGameDateFrom = useCallback(
    (date: Date | null) => {
      if (date && gameDateTo && date > gameDateTo) {
        setGameDateTo(date);
      }
      setGameDateFrom(date);
    },
    [gameDateFrom, gameDateTo],
  );

  const onChangeGameDateTo = useCallback(
    (date: Date | null) => {
      if (date && gameDateFrom && date < gameDateFrom) {
        setGameDateFrom(date);
      }
      setGameDateTo(date);
    },
    [gameDateFrom, gameDateTo],
  );

  const onClearRecords = useCallback(() => {
    setRecords([]);
    setGameDateFrom(null);
    setGameDateTo(null);
    setSeason('all');
    setKeywordsByPlayer('');
  }, []);

  const onDeleteRecord = useCallback(
    (record: string) => {
      const param: string[] = [...records];
      const index = param.indexOf(record);
      const first = param.slice(0, index);
      const second = param.slice(index + 1);
      setRecords([...first, ...second]);
    },
    [records],
  );

  const onDeleteDateFrom = useCallback(() => {
    setGameDateFrom(null);
  }, [gameDateFrom]);

  const onDeleteDateTo = useCallback(() => {
    setGameDateTo(null);
  }, [gameDateTo]);

  const onChangeKeywordByPlayer = useCallback((event: React.ChangeEvent<{ value: string }>) => {
    const { value } = event.currentTarget;
    setKeywordsByPlayer(value);
  }, []);

  return (
    <Container component="article" className={classes.root}>
      {/*기록*/}
      <Grid container className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>기록</Typography>
        </Grid>
        <Grid item xs className={classes.filterContainer}>
          <Hidden mdDown>
            {(isShowRecord ? ScriptOptions : ScriptOptions.slice(0, 6)).map(record => {
              const index = records.indexOf(record.value);
              const isSelected = index !== -1;

              return (
                <HtmlTooltip key={record.id} title={record.name} placement="bottom">
                  <button
                    className={clsx(classes.season, isSelected && classes.selected)}
                    value={record.value}
                    onClick={onClickRecord}
                  >
                    {record.name}
                  </button>
                </HtmlTooltip>
              );
            })}
          </Hidden>
          <Hidden lgUp>
            <RecordSelect selectedRecords={records} onClickRecord={onSelectRecord} />
          </Hidden>
        </Grid>
        <Hidden mdDown>
          <Grid
            item
            onClick={() => {
              setIsShowRecord(!isShowRecord);
            }}
            style={{
              transform: `rotate(${isShowRecord ? 180 : 0}deg)`,
            }}
            className={classes.moreButton}
          >
            <ExpandMoreIcon />
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*시즌*/}
      <Grid container className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>시즌</Typography>
        </Grid>
        <Grid item xs className={classes.filterContainer}>
          <Hidden mdDown>
            <button
              className={clsx(classes.season, String(season) === 'all' && classes.selected)}
              value="all"
              onClick={onClickSeason}
            >
              모두
            </button>
            {(isShowSeason ? seasonList : seasonList.slice(0, 5)).map((seasonItem: CodeVO) => (
              <HtmlTooltip key={seasonItem.id} title={seasonItem.name} placement="bottom">
                <button
                  className={clsx(
                    classes.season,
                    String(seasonItem.id) === String(season) && classes.selected,
                  )}
                  value={seasonItem.id}
                  onClick={onClickSeason}
                >
                  {seasonItem.name}
                </button>
              </HtmlTooltip>
            ))}
          </Hidden>
          <Hidden lgUp>
            <SeasonSelect seasonId={season} seasonList={seasonList} onChangeSeason={setSeason} />
          </Hidden>
        </Grid>
        <Hidden mdDown>
          <Grid
            item
            onClick={() => {
              setIsShowSeason(!isShowSeason);
            }}
            style={{
              transform: `rotate(${isShowSeason ? 180 : 0}deg)`,
            }}
            className={classes.moreButton}
          >
            <ExpandMoreIcon />
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*기간*/}
      <Grid container className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>기간</Typography>
        </Grid>
        <Grid item xs className={clsx(classes.filterContainer, classes.dateContainer)}>
          <FromToDate
            from={gameDateFrom}
            to={gameDateTo}
            onChangeFrom={onChangeGameDateFrom}
            onChangeTo={onChangeGameDateTo}
          />
        </Grid>
      </Grid>

      <Divider />

      {/*검색어*/}
      <Grid container className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>검색어</Typography>
        </Grid>
        <Grid item xs className={clsx(classes.inputPlayerNameContainer, classes.filterContainer)}>
          <form onSubmit={onSubmitSearch}>
            <InputBase
              type="search"
              className={classes.inputPlayerName}
              placeholder="검색어를 입력해주세요."
              onChange={onChangeKeywordByPlayer}
              value={keywordsByPlayer}
            />
          </form>
        </Grid>
        <Hidden xsDown>
          <Grid item xs className={classes.deselectionButtonContainer}>
            <Button className={classes.deselectionButton} onClick={onClearRecords}>
              <img src={CancelIcon}></img>
              <Typography className={classes.title}>선택해제</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs
            container
            justifyContent="flex-end"
            className={classes.searchButtonContainer}
          >
            <Button
              className={classes.searchButton}
              startIcon={<img src={SearchIcon} />}
              onClick={onClickSearch}
            >
              검색
            </Button>
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*모바일에서 검색버튼*/}
      <Hidden smUp>
        <Grid container justifyContent="flex-end" className={clsx(classes.container)}>
          <Grid item xs className={classes.deselectionButtonContainer}>
            <Button className={classes.deselectionButton} onClick={onClearRecords}>
              <img src={CancelIcon}></img>
              <Typography className={classes.title}>선택해제</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs
            container
            justifyContent="flex-end"
            className={classes.searchButtonContainer}
          >
            <Button
              className={classes.searchButton}
              startIcon={<img src={SearchIcon} />}
              onClick={onClickSearch}
            >
              검색
            </Button>
          </Grid>
        </Grid>
      </Hidden>
    </Container>
  );
}

export default SearchFilterByRecord;
